import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Box, Button, Typography } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  type AccountAllocationCsvIssue,
  hasBlockingIssue,
  MAX_ACCOUNT_ALLOCATION_CSV_FILE_MB,
  MAX_ACCOUNT_ALLOCATION_CSV_FUND_ROWS,
  type ParsedAccountAllocationCsv,
} from './parseAccountAllocationCsv';

const NS = 'components:accountAllocationGrid.csvUpload';

const issueMessage = (issue: AccountAllocationCsvIssue, t: TFunction) =>
  t(`${NS}.issues.${issue.code}`, {
    row: issue.row,
    column: issue.column,
    fund: issue.fund,
    accountNumber: issue.accountNumber || t(`${NS}.blankAccount`),
    value: issue.value,
    maxRows: MAX_ACCOUNT_ALLOCATION_CSV_FUND_ROWS,
    maxMb: MAX_ACCOUNT_ALLOCATION_CSV_FILE_MB,
  });

// Errors first: they are why nothing was applied.
const errorsFirst = (issues: AccountAllocationCsvIssue[]) => [
  ...issues.filter((issue) => issue.severity === 'error'),
  ...issues.filter((issue) => issue.severity === 'warning'),
];

export type AccountAllocationCsvReviewDialogProp = {
  review: ParsedAccountAllocationCsv | null;
  onImport: () => void;
  onDismiss: () => void;
};

// Errors block the upload, so they offer only Close. Warnings leave the choice
// to the advisor.
export const AccountAllocationCsvReviewDialog = ({
  review,
  onImport,
  onDismiss,
}: AccountAllocationCsvReviewDialogProp) => {
  const { t } = useTranslation();
  const blocked = review ? hasBlockingIssue(review.issues) : false;
  const tone = blocked ? 'error' : 'warning';

  return (
    <Dialog open={review !== null} onClose={onDismiss} maxWidth='sm' fullWidth>
      <DialogTitle onClose={onDismiss}>{t(`${NS}.${tone}Title`)}</DialogTitle>
      <DialogContent>
        <Typography variant='bodyMedium' component='p'>
          {t(`${NS}.${tone}Intro`)}
        </Typography>
        <Box component='ul' sx={{ pl: 3, mb: 0 }}>
          {errorsFirst(review?.issues ?? []).map((issue) => (
            <Typography
              key={`${issue.code}-${issue.row}-${issue.column}-${issue.subAccountId}`}
              variant='bodyMedium'
              component='li'
            >
              {issueMessage(issue, t)}
            </Typography>
          ))}
        </Box>
      </DialogContent>
      <DialogFooter>
        {blocked ? (
          <Button type='button' variant='contained' onClick={onDismiss}>
            {t('shared:close')}
          </Button>
        ) : (
          <>
            <Button type='button' variant='outlined' onClick={onDismiss}>
              {t('shared:cancel')}
            </Button>
            <Button type='button' variant='contained' onClick={onImport}>
              {t('shared:import')}
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
};
